import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import logo from "@/assets/logo.png";

const AuthMfa = () => {
  const { user, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [recoveryCode, setRecoveryCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    document.title = "Two-factor verification | emrkt.co";
  }, []);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/auth", { replace: true });
      return;
    }
    if (!user) return;
    (async () => {
      const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (aal && aal.currentLevel === aal.nextLevel) {
        navigate("/", { replace: true });
        return;
      }
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const totp = factors?.totp?.find((f) => f.status === "verified");
      if (totp) setFactorId(totp.id);
    })();
  }, [user, loading, navigate]);

  const handleVerify = async () => {
    if (!factorId || code.length < 6) return;
    setVerifying(true);
    const { data: challenge, error: cErr } = await supabase.auth.mfa.challenge({ factorId });
    if (cErr || !challenge) {
      setVerifying(false);
      toast.error(cErr?.message || "Could not start challenge");
      return;
    }
    const { error: vErr } = await supabase.auth.mfa.verify({
      factorId,
      challengeId: challenge.id,
      code,
    });
    setVerifying(false);
    if (vErr) {
      toast.error(vErr.message || "Invalid code");
      return;
    }
    navigate("/", { replace: true });
  };

  const handleRecovery = async () => {
    if (recoveryCode.trim().length < 8) {
      toast.error("Enter a recovery code");
      return;
    }
    setRedeeming(true);
    const { data, error } = await supabase.functions.invoke("mfa-consume-recovery-code", {
      body: { code: recoveryCode.trim() },
    });
    setRedeeming(false);
    if (error || (data && (data as any).error)) {
      toast.error((data as any)?.error || error?.message || "Invalid recovery code");
      return;
    }
    toast.success("Recovery code accepted. Re-enroll a new authenticator.");
    navigate("/account?tab=security&enroll=1", { replace: true });
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-elegant">
        <div className="flex flex-col items-center mb-6">
          <img src={logo} alt="emrkt logo" className="h-10 w-10 mb-3" />
          <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <h1 className="text-xl font-bold text-foreground">Two-factor verification</h1>
          <p className="mt-1 text-sm text-muted-foreground text-center">
            Enter the 6-digit code from your authenticator app.
          </p>
        </div>
        <div className="space-y-3">
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456"
            className="text-center text-lg tracking-[0.4em]"
          />
          <Button className="w-full" onClick={handleVerify} disabled={verifying || code.length < 6 || !factorId}>
            {verifying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Verify
          </Button>
          <div className="flex items-center justify-between text-xs">
            <Dialog>
              <DialogTrigger asChild>
                <button className="text-muted-foreground hover:text-foreground underline-offset-4 hover:underline">
                  Use a recovery code
                </button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Use a recovery code</DialogTitle>
                  <DialogDescription>
                    Each recovery code can be used once. Using one removes your current authenticator so you can enroll a new device.
                  </DialogDescription>
                </DialogHeader>
                <Input
                  value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                  placeholder="ABCDE-12345"
                />
                <DialogFooter>
                  <Button onClick={handleRecovery} disabled={redeeming}>
                    {redeeming && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Redeem code
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <button
              className="text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
              onClick={() => signOut()}
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthMfa;